import type { ReactNode } from "react";
import styles from "./Callout.module.css";

export type CalloutTone = "note" | "warning" | "error";

/**
 * The shared surface for empty and error states. Copy rules (canon design skill): say what
 * happened and what to do next, in the interface's voice — never apologise, never be vague.
 *
 * `error` announces itself assertively; `note` and `warning` are polite live regions so a
 * background refresh does not interrupt a screen-reader user mid-sentence.
 */
export function Callout({
  tone = "note",
  title,
  children,
  actions,
}: {
  tone?: CalloutTone | undefined;
  title: string;
  children?: ReactNode | undefined;
  actions?: ReactNode | undefined;
}) {
  return (
    <div
      className={`${styles.callout} ${styles[tone]}`}
      role={tone === "error" ? "alert" : "status"}
    >
      <p className={styles.title}>
        <svg
          className={styles.marker}
          viewBox="0 0 16 16"
          aria-hidden="true"
          focusable="false"
        >
          <circle cx="8" cy="8" r="7" fill="none" stroke="currentColor" strokeWidth="1.4" />
          {tone === "note" ? (
            <path
              d="M8 7.2v4M8 4.6v.9"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          ) : (
            <path
              d="M8 4.6v4.2M8 11v.9"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
          )}
        </svg>
        {title}
      </p>
      {children ? <div className={styles.body}>{children}</div> : null}
      {actions ? <div className={styles.actions}>{actions}</div> : null}
    </div>
  );
}
